import { TranslationKey } from './../../../../types/translation/TranslationKey';

import { Grid } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import { useMutation } from '@apollo/client';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import ProductMasterDataInfoPane from './Panes/ProductMasterDataInfoPane';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import LoadingPackage from '../../Common/LoadingPackage';
import {
  ProductMasterDataMutations,
  UpdateProductMasterDataResponse,
  UpdateProductMasterDataVariables,
} from '../../../../graphql/productMasterData.graphql';
import ProductMasterDataAttributePane from './Panes/ProductMasterDataAttributePane';
import ProductMasterDataCommentPane from './Panes/ProductMasterDataCommentPane';
import { testIds } from '../../../../util/identifiers/identifiers.util';
import ProductMasterDataReorderRulesPane from './Panes/ProductMasterDataReorderRulesPane';
import NotFound from '../../../../assets/html/notFound';
import { CompanyContext } from '../../../../context/CompanyContext';
import Pane from '../../../../VentoryUI/components/common/Pane';
import FilesForEntityPane from '../../../../VentoryUI/components/common/File/FilesForEntityPane';
import { FileEntityType } from '../../../../types/file';
import { Routing } from '../../../../util/routes.util';
import { EntityTagMultiSelect } from '../../../components/Dropdown';
import { EntityType } from '../../../../types/comment';
import HandlingUnitsOverviewPane from '../Settings/Panes/HandlingUnits/HandlingUnitsOverviewPane';
import ProductMasterDataLabelsPane from './Panes/ProductMasterDataLabelsPane';
import ProductMasterDataKitPane from './Panes/ProductMasterDataKitPane';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import GenerateBarcodeButton from '../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { BackButton, ButtonVariant, SaveButton } from '../../../components/Button';
import { ProductMasterData, ProductMasterDataInput } from '../../../../types/product_master_data';

const tabs = (productMasterData: ProductMasterData, isAdmin: boolean) =>
  [
    {
      text: t(TranslationKey.info).singular.label,
      path: 'info',
      key: 'info',
    },
    {
      text: t(TranslationKey.attributes).singular.label,
      path: 'attributes',
      key: 'attributes',
    },
    {
      text: t(TranslationKey.label).plural.label,
      path: 'labels',
      key: 'labels',
    },
    {
      text: t(TranslationKey.comment).plural.label,
      path: 'comments',
      key: 'comments',
    },
    {
      text: t(TranslationKey.stockTriggers).singular.label,
      path: 'stock_triggers',
      key: 'stock_triggers',
    },
    {
      text: t(TranslationKey.filesAndAssets).singular.label,
      path: 'files',
      key: 'files',
    },
    {
      text: t(TranslationKey.handlingUnit).plural.label,
      path: 'handling_units',
      key: 'handling_units',
    },
    {
      text: t(TranslationKey.kit).plural.label,
      path: 'kits',
      key: 'kits',
    },
  ].filter(tab => {
    if (tab.key === 'kits' && !productMasterData.kitManaged) return false;
    if (tab.key === 'handling_units' && productMasterData.handlingUnit) return false;

    if (!isAdmin && tab.key !== 'info' && tab.key !== 'attributes' && tab.key !== 'files') return false;

    return true;
  });

export default function UpdateProductMasterDataPane({}) {
  const navigate = useNavigate();

  const id = useParams()['id'] || '';

  const { productMasterData, setProductMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { currentCompany } = useContext(CompanyContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  const [error, setError] = useState<string>('');

  const existing = productMasterData.get(id);

  const [productMasterDataInput, setProductMasterDataInput] = useState<ProductMasterDataInput>(
    new ProductMasterDataInput(existing) || new ProductMasterDataInput({ companyId: currentCompany.id }),
  );

  const [update, { loading }] = useMutation<UpdateProductMasterDataResponse, UpdateProductMasterDataVariables>(
    ProductMasterDataMutations.update,
    {
      onCompleted: res => {
        const pmd = res.updateProductMasterData[0];
        setProductMasterData(new Map(productMasterData).set(pmd.id, new ProductMasterData(pmd)));
        navigate(Routing.productMasterData.overview());
      },
    },
  );

  useEffect(() => {
    if (!productMasterDataInput.id) {
      setProductMasterDataInput(
        new ProductMasterDataInput(productMasterData.get(id)) ||
          new ProductMasterData({ companyId: currentCompany.id }),
      );
    }
  }, [productMasterData]);

  if (!existing) return <NotFound />;

  const handleUpdate = async (pmd: ProductMasterDataInput) => {
    try {
      await update({
        variables: {
          productMasterData: [pmd.forUpdate()],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const footer = (input?: ProductMasterData) => (
    <div className='flex items-center justify-between gap-2'>
      <BackButton disabled={loading} onClick={() => navigate(Routing.productMasterData.overview())} />
      <div className='flex items-center gap-2'>
        <GenerateBarcodeButton
          variant={ButtonVariant.secondary}
          entityType={BarcodeExportEntityType.productMasterData}
          entityIds={[id]}
        />
        {isAdmin ? (
          <SaveButton
            loading={loading}
            onClick={() => handleUpdate(productMasterDataInput)}
            disabled={!productMasterDataInput.productName || !productMasterDataInput.productNumber}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <Pane error={error} tabs={tabs(productMasterDataInput, isAdmin)} testId={testIds.updateProductMasterDataPane}>
      {productMasterDataLoading && !productMasterData.get(id) ? (
        <Grid container alignItems={'center'}>
          <Grid item className='fill-gray-300 ' marginX={'auto'}>
            <LoadingPackage />
          </Grid>
        </Grid>
      ) : (
        <Routes>
          <Route
            path='info'
            element={
              <ProductMasterDataInfoPane
                productMasterDataInput={productMasterDataInput}
                setProductMasterDataInput={setProductMasterDataInput}
                tagInput={<EntityTagMultiSelect entityId={productMasterDataInput.id} entityType={EntityType.pmd} />}
                footer={footer()}
              />
            }
          />
          <Route
            path='attributes'
            element={
              <ProductMasterDataAttributePane
                input={productMasterDataInput}
                setInput={setProductMasterDataInput}
                // footer={footer}
              />
            }
          />
          <Route
            path='comments'
            element={<ProductMasterDataCommentPane productMasterData={productMasterData.get(id)} footer={footer} />}
          />
          <Route path='kits' element={<ProductMasterDataKitPane pmdId={id} footer={footer} />} />
          <Route path='labels' element={<ProductMasterDataLabelsPane pmdId={id} footer={footer} />} />
          <Route
            path='stock_triggers/*'
            element={<ProductMasterDataReorderRulesPane productMasterData={productMasterData.get(id)} />}
          />
          <Route
            path='files'
            element={<FilesForEntityPane entityId={id} entityType={FileEntityType.pmd} setError={setError} />}
          />
          <Route path='handling_units' element={<HandlingUnitsOverviewPane pmdId={id} />} />
        </Routes>
      )}
    </Pane>
  );
}
